/**
 * Shared project-state colour language.
 *
 * Extracted from `ProjectStatusChip` (SB2-W-16) so any surface that needs the
 * canonical project-state colours — the chip itself AND the Ongoing-Projects
 * dashboard distribution band (R0627-02/03) — sources them from ONE place
 * rather than re-deriving hexes. Christian's locked colour matrix:
 *
 *   white  ← OFFER, DATA_IMPORT
 *   yellow ← DATA_VALIDATION_CUSTOMER, PREPARATION, EXECUTION, ACCOUNTING,
 *            PAYMENT
 *   green  ← COMPLETED
 *   red    ← WITHDRAWN, REJECTED
 *
 * D81 — the bands survive as the DOMAIN vocabulary (they are what `data-color`
 * publishes and what the state matrix is written in), but they no longer carry
 * colour. Each band now resolves to one of D81's five semantic SLOTS, and the
 * slot resolves to real values through the shared `statusChipStyle` recipe. No
 * hex or `rgba()` is written here any more.
 */
import type { Theme } from "@mui/material/styles";
import {
  alphaOver,
  color,
  statusChipStyle,
  type StatusSlot,
} from "@aeriosiq/ui";
import type { ProjectState } from "./types.js";

export type ProjectColorBand = "white" | "yellow" | "green" | "red";

export const PROJECT_STATE_COLOR: Record<ProjectState, ProjectColorBand> = {
  OFFER: "white",
  DATA_IMPORT: "white",
  DATA_VALIDATION_CUSTOMER: "yellow",
  PREPARATION: "yellow",
  EXECUTION: "yellow",
  ACCOUNTING: "yellow",
  PAYMENT: "yellow",
  COMPLETED: "green",
  WITHDRAWN: "red",
  REJECTED: "red",
};

/**
 * Band → D81 slot. The band names are historical; the SLOT is the meaning:
 *
 *   white  → neutral   — OFFER / DATA_IMPORT are inert, nothing has started
 *   yellow → attention — the whole mid-lifecycle is "a human has to act"
 *   green  → success   — COMPLETED, finished cleanly
 *   red    → critical  — WITHDRAWN / REJECTED, terminal and blocking
 */
const SLOT_BY_BAND: Record<ProjectColorBand, StatusSlot> = {
  white: "neutral",
  yellow: "attention",
  green: "success",
  red: "critical",
};

export const PROJECT_STATE_LABEL: Record<ProjectState, string> = {
  OFFER: "Offer",
  DATA_IMPORT: "Data import",
  DATA_VALIDATION_CUSTOMER: "Data validation",
  PREPARATION: "Preparation",
  EXECUTION: "Execution",
  ACCOUNTING: "Accounting",
  PAYMENT: "Payment",
  COMPLETED: "Completed",
  WITHDRAWN: "Withdrawn",
  REJECTED: "Rejected",
};

export interface ProjectBandStyle {
  backgroundColor: string;
  color: string;
  /** Under D81 EVERY band has a keyline — the fill, the keyline and the dot
   *  are the three signals that carry the hue, because the label may not
   *  (D26). The field stays optional so no consumer has to change. */
  border?: string;
}

/**
 * Resolve a project-state colour band to its background + foreground + keyline.
 *
 * Delegates to D81's `statusChipStyle`: the hue owns the fill and the keyline,
 * and the LABEL IS NEUTRAL (`chipLabel`). The previous implementation put a
 * darkened status colour in the text and hand-tuned an `rgba()` fill per band —
 * measured and rejected by D81, because a tonal fill moves the ground toward
 * the label and the contrast measurement collapses.
 */
export function projectBandStyle(
  theme: Theme,
  band: ProjectColorBand,
): ProjectBandStyle {
  const chip = statusChipStyle(theme, SLOT_BY_BAND[band]);
  return {
    backgroundColor: chip.backgroundColor,
    color: chip.color,
  };
}

/**
 * Distribution-bar ramp (R0627-03 follow-up, 2026-06-28; re-based on the Aerios
 * tokens under D81/D82).
 *
 * The four-band chip palette (`projectBandStyle`) collapses five of the seven
 * *ongoing* project states onto ONE slot — so the Ongoing-Projects overall band
 * read as a single block (Christian: *"you really do not see any difference…"*).
 *
 * The lifecycle is a PROGRESSION, so this is a **sequential ramp**, not a
 * categorical hue-soup — and under Aerios that constraint gets stronger, not
 * weaker: P3 makes aqua the only colour permitted to signal interaction, live
 * state, data or current position, so a lifecycle bar may not reach for a
 * second accent. The ramp is therefore ONE hue — `--status-neutral`, the
 * system's own inert grey — stepped in evenly-spaced alpha over the page
 * surface, ending on the accent (`primary`) at PAYMENT, the "in the money /
 * about to close" end. `alphaOver` flattens each step to a real colour so a
 * segment is a measurable value rather than an unmeasurable `rgba()`.
 *
 * Foregrounds follow D81's chip rule: the tinted steps take the neutral
 * `chipLabel`; the one SOLID accent segment takes `text.inverse`, which is the
 * D78 consequence that a filled accent surface inverts its label between modes
 * rather than merely changing hue. Terminal states are defined for completeness
 * but never render in the ongoing band; they take the raw slot hue solid (they
 * are outside the ramp, so they must not read as a ramp step).
 */
export interface ProjectStateDistributionStyle {
  backgroundColor: string;
  color: string;
}

interface Pair {
  bg: string;
  fg: string;
}

function distributionRamp(mode: "light" | "dark"): Record<ProjectState, Pair> {
  const c = color[mode];
  /** One ramp step. The alphas are evenly spaced (0.09 apart) and monotonic in
   *  BOTH modes, because the tint always moves AWAY from the ground — darker on
   *  light, lighter on dark. */
  const step = (alpha: number): Pair => ({
    bg: alphaOver(c.status.neutral, alpha, c.surface.default),
    fg: c.chipLabel,
  });
  /** A state OUTSIDE the ramp: the raw slot hue, solid, inverse label. */
  const solid = (hue: string): Pair => ({ bg: hue, fg: c.text.inverse });
  return {
    OFFER: step(0.08),
    DATA_IMPORT: step(0.17),
    DATA_VALIDATION_CUSTOMER: step(0.26),
    PREPARATION: step(0.35),
    EXECUTION: step(0.44),
    ACCOUNTING: step(0.53),
    // The terminus of the progression — the accent, solid.
    PAYMENT: { bg: c.primary, fg: c.text.inverse },
    COMPLETED: solid(c.status.success),
    // WITHDRAWN stays NEUTRAL rather than critical here (unlike the chip): in a
    // distribution it is counted as closed, and a red alarm colour would misread
    // as an active problem (Christian, 2026-06-28).
    WITHDRAWN: solid(c.status.neutral),
    REJECTED: solid(c.status.critical),
  };
}

const DISTRIBUTION_LIGHT: Record<ProjectState, Pair> =
  distributionRamp("light");
const DISTRIBUTION_DARK: Record<ProjectState, Pair> = distributionRamp("dark");

export function projectStateDistributionStyle(
  theme: Theme,
  state: ProjectState,
): ProjectStateDistributionStyle {
  const pair = (
    theme.palette.mode === "dark" ? DISTRIBUTION_DARK : DISTRIBUTION_LIGHT
  )[state];
  return { backgroundColor: pair.bg, color: pair.fg };
}
